/**
 * TextElementEditor Component
 * Rich text editor for Text Elements using TipTap
 * Supports formatting, variable insertion, and preview mode
 */

import { useState, useCallback, useEffect, useMemo } from 'react'
import { useEditor, EditorContent, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Placeholder from '@tiptap/extension-placeholder'
import CharacterCount from '@tiptap/extension-character-count'
import Highlight from '@tiptap/extension-highlight'
import { Mark, mergeAttributes } from '@tiptap/core'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Undo,
  Redo,
  Eye,
  Edit3,
  Code,
  Minus,
  Braces,
} from 'lucide-react'
import { VariablePicker, type Variable } from './VariablePicker'

// ============================================
// Types
// ============================================

export interface TextElementEditorProps {
  value: string // HTML content
  onChange: (html: string, plainText: string, variables: string[]) => void
  placeholder?: string
  minHeight?: number
  readOnly?: boolean
  className?: string
  customVariables?: Variable[]
}

// ============================================
// Custom TipTap Extension for Variable Highlighting
// ============================================

/**
 * Custom mark extension to highlight template variables in the editor
 * Variables are in {{variableName}} format
 */
const VariableMark = Mark.create({
  name: 'variable',

  addOptions() {
    return {
      HTMLAttributes: {
        class: 'variable-highlight',
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span.variable-highlight',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0]
  },
})

// ============================================
// Formatting Toolbar Component
// ============================================

interface ToolbarButtonProps {
  onClick: () => void
  active?: boolean
  disabled?: boolean
  tooltip: string
  children: React.ReactNode
}

function ToolbarButton({
  onClick,
  active = false,
  disabled = false,
  tooltip,
  children,
}: ToolbarButtonProps) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClick}
            disabled={disabled}
            className={cn(
              'h-8 w-8 p-0 rounded-md',
              active && 'bg-neutral-200 dark:bg-neutral-700'
            )}
          >
            {children}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

interface FormattingToolbarProps {
  editor: Editor | null
  onInsertVariable: (variable: Variable) => void
  disabled?: boolean
  customVariables?: Variable[]
}

function FormattingToolbar({
  editor,
  onInsertVariable,
  disabled = false,
  customVariables = [],
}: FormattingToolbarProps) {
  if (!editor) return null

  return (
    <div className="flex items-center gap-0.5 flex-wrap p-1.5 border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 rounded-t-xl">
      {/* Text Formatting */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive('bold')}
        disabled={disabled}
        tooltip="Bold (Ctrl+B)"
      >
        <Bold className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive('italic')}
        disabled={disabled}
        tooltip="Italic (Ctrl+I)"
      >
        <Italic className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        active={editor.isActive('underline')}
        disabled={disabled}
        tooltip="Underline (Ctrl+U)"
      >
        <UnderlineIcon className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        active={editor.isActive('strike')}
        disabled={disabled}
        tooltip="Strikethrough"
      >
        <Strikethrough className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCode().run()}
        active={editor.isActive('code')}
        disabled={disabled}
        tooltip="Inline Code"
      >
        <Code className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHighlight().run()}
        active={editor.isActive('highlight')}
        disabled={disabled}
        tooltip="Highlight"
      >
        <span className="h-4 w-4 flex items-center justify-center rounded bg-yellow-200 dark:bg-yellow-500/50 text-xs font-bold">
          H
        </span>
      </ToolbarButton>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Headings */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        active={editor.isActive('heading', { level: 1 })}
        disabled={disabled}
        tooltip="Heading 1"
      >
        <Heading1 className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        active={editor.isActive('heading', { level: 2 })}
        disabled={disabled}
        tooltip="Heading 2"
      >
        <Heading2 className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        active={editor.isActive('heading', { level: 3 })}
        disabled={disabled}
        tooltip="Heading 3"
      >
        <Heading3 className="h-4 w-4" />
      </ToolbarButton>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Lists */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive('bulletList')}
        disabled={disabled}
        tooltip="Bullet List"
      >
        <List className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive('orderedList')}
        disabled={disabled}
        tooltip="Numbered List"
      >
        <ListOrdered className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        active={editor.isActive('blockquote')}
        disabled={disabled}
        tooltip="Block Quote"
      >
        <Quote className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        disabled={disabled}
        tooltip="Horizontal Rule"
      >
        <Minus className="h-4 w-4" />
      </ToolbarButton>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Undo/Redo */}
      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={disabled || !editor.can().undo()}
        tooltip="Undo (Ctrl+Z)"
      >
        <Undo className="h-4 w-4" />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={disabled || !editor.can().redo()}
        tooltip="Redo (Ctrl+Y)"
      >
        <Redo className="h-4 w-4" />
      </ToolbarButton>

      <Separator orientation="vertical" className="h-6 mx-1" />

      {/* Variable Picker */}
      <VariablePicker
        onSelect={onInsertVariable}
        customVariables={customVariables}
        disabled={disabled}
      />
    </div>
  )
}

// ============================================
// Preview Component
// ============================================

interface PreviewPaneProps {
  html: string
  className?: string
}

function PreviewPane({ html, className }: PreviewPaneProps) {
  // Process HTML to highlight variables in preview
  const processedHtml = useMemo(() => {
    return html.replace(
      /\{\{(\w+)\}\}/g,
      '<span class="variable-preview">{{$1}}</span>'
    )
  }, [html])

  return (
    <div
      className={cn(
        'prose prose-neutral dark:prose-invert max-w-none',
        'p-4 min-h-[200px]',
        className
      )}
      dangerouslySetInnerHTML={{ __html: processedHtml }}
    />
  )
}

// ============================================
// Stats Footer Component
// ============================================

interface EditorStatsProps {
  characters: number
  words: number
  variables: string[]
}

function EditorStats({ characters, words, variables }: EditorStatsProps) {
  return (
    <div className="flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400 px-3 py-2 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 rounded-b-xl">
      <div className="flex items-center gap-4">
        <span>{characters.toLocaleString()} characters</span>
        <span>{words.toLocaleString()} words</span>
      </div>
      {variables.length > 0 && (
        <div className="flex items-center gap-1.5">
          <Braces className="h-3.5 w-3.5" />
          <span>{variables.length} variable{variables.length !== 1 ? 's' : ''}</span>
        </div>
      )}
    </div>
  )
}

// ============================================
// Helper Functions
// ============================================

/**
 * Extract all variable names from HTML content
 */
function extractVariables(html: string): string[] {
  const regex = /\{\{(\w+)\}\}/g
  const matches: string[] = []
  let match: RegExpExecArray | null

  while ((match = regex.exec(html)) !== null) {
    const varName = match[1]
    if (varName && !matches.includes(varName)) {
      matches.push(varName)
    }
  }

  return matches
}

/**
 * Count words in text
 */
function countWords(text: string): number {
  return text
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0).length
}

// ============================================
// TextElementEditor Component
// ============================================

export function TextElementEditor({
  value,
  onChange,
  placeholder = 'Start typing your content here. Use {{variableName}} for dynamic content...',
  minHeight = 200,
  readOnly = false,
  className,
  customVariables = [],
}: TextElementEditorProps) {
  const [isPreviewMode, setIsPreviewMode] = useState(false)

  // Initialize TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      Highlight.configure({
        multicolor: false,
      }),
      Placeholder.configure({
        placeholder,
        emptyEditorClass: 'is-editor-empty',
      }),
      CharacterCount,
      VariableMark,
    ],
    content: value,
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML()
      const plainText = editor.getText()
      const variables = extractVariables(html)
      onChange(html, plainText, variables)
    },
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-neutral dark:prose-invert max-w-none',
          'focus:outline-none',
          'p-4'
        ),
        style: `min-height: ${minHeight}px`,
      },
    },
  })

  // Update editor content when value changes externally
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value)
    }
  }, [editor, value])

  // Update editor editable state
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly && !isPreviewMode)
    }
  }, [editor, readOnly, isPreviewMode])

  // Handle variable insertion
  const handleInsertVariable = useCallback(
    (variable: Variable) => {
      if (!editor) return

      const variableText = `{{${variable.name}}}`
      editor.chain().focus().insertContent(variableText).run()
    },
    [editor]
  )

  // Calculate statistics
  const characters = editor?.storage.characterCount.characters() ?? 0
  const plainText = editor?.getText() ?? ''
  const words = countWords(plainText)
  const variables = extractVariables(editor?.getHTML() ?? '')

  return (
    <div
      className={cn(
        'border border-neutral-200 dark:border-neutral-700 rounded-xl overflow-hidden',
        'bg-white dark:bg-neutral-950',
        className
      )}
    >
      {/* Toolbar */}
      {!readOnly && (
        <div className="flex items-center justify-between">
          <FormattingToolbar
            editor={editor}
            onInsertVariable={handleInsertVariable}
            disabled={isPreviewMode}
            customVariables={customVariables}
          />

          {/* Preview Toggle */}
          <div className="pr-2">
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsPreviewMode(!isPreviewMode)}
                    className={cn(
                      'h-8 gap-1.5 px-2.5 rounded-lg',
                      isPreviewMode && 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                    )}
                  >
                    {isPreviewMode ? (
                      <>
                        <Edit3 className="h-4 w-4" />
                        <span className="text-sm">Edit</span>
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4" />
                        <span className="text-sm">Preview</span>
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  {isPreviewMode ? 'Switch to edit mode' : 'Preview rendered content'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      )}

      {/* Editor / Preview Content */}
      <div className="relative">
        {isPreviewMode ? (
          <PreviewPane
            html={editor?.getHTML() ?? ''}
            className={`min-h-[${minHeight}px]`}
          />
        ) : (
          <EditorContent editor={editor} />
        )}
      </div>

      {/* Stats Footer */}
      <EditorStats characters={characters} words={words} variables={variables} />

      {/* Editor Styles */}
      <style>{`
        .ProseMirror {
          outline: none;
        }

        .ProseMirror p.is-editor-empty:first-child::before {
          color: #adb5bd;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }

        .ProseMirror .variable-highlight,
        .variable-preview {
          background: linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%);
          color: #1d4ed8;
          padding: 1px 4px;
          border-radius: 4px;
          font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
          font-size: 0.9em;
          font-weight: 500;
        }

        .dark .ProseMirror .variable-highlight,
        .dark .variable-preview {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(99, 102, 241, 0.2) 100%);
          color: #93c5fd;
        }

        .ProseMirror h1 {
          font-size: 1.75rem;
          font-weight: 700;
          margin-top: 1rem;
          margin-bottom: 0.5rem;
        }

        .ProseMirror h2 {
          font-size: 1.5rem;
          font-weight: 600;
          margin-top: 0.875rem;
          margin-bottom: 0.5rem;
        }

        .ProseMirror h3 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-top: 0.75rem;
          margin-bottom: 0.5rem;
        }

        .ProseMirror ul,
        .ProseMirror ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }

        .ProseMirror li {
          margin: 0.25rem 0;
        }

        .ProseMirror blockquote {
          border-left: 3px solid #e5e7eb;
          padding-left: 1rem;
          margin: 0.5rem 0;
          color: #6b7280;
          font-style: italic;
        }

        .dark .ProseMirror blockquote {
          border-left-color: #374151;
          color: #9ca3af;
        }

        .ProseMirror code {
          background-color: #f3f4f6;
          color: #1f2937;
          padding: 0.125rem 0.25rem;
          border-radius: 0.25rem;
          font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
          font-size: 0.875em;
        }

        .dark .ProseMirror code {
          background-color: #374151;
          color: #e5e7eb;
        }

        .ProseMirror mark {
          background-color: #fef08a;
          color: inherit;
          border-radius: 0.125rem;
          padding: 0 0.125rem;
        }

        .dark .ProseMirror mark {
          background-color: rgba(250, 204, 21, 0.3);
        }

        .ProseMirror hr {
          border: none;
          border-top: 1px solid #e5e7eb;
          margin: 1rem 0;
        }

        .dark .ProseMirror hr {
          border-top-color: #374151;
        }

        .ProseMirror p {
          margin: 0.5rem 0;
        }

        .ProseMirror p:first-child {
          margin-top: 0;
        }

        .ProseMirror p:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </div>
  )
}

export default TextElementEditor
